<template>
    <div class="space-y-8">
        <section class="space-y-5">
            <div v-if="profileId" class="flex items-center gap-4">
                <InputAvatarUpload
                    :model-value="state.photo_path ?? undefined"
                    @update:model-value="update('photo_path', $event || null)"
                    bucket="doctor-photos"
                    :folder="profileId"
                    :upload-url="`/api/telemedicine/doctors/${profileId}/photo`"
                    :placeholder="state.full_name"
                />
                <div>
                    <p class="text-sm font-medium text-gray-700">Profile photo</p>
                    <p class="text-xs text-gray-500">Shown to patients in the SAMU app. A square, well-lit headshot works best.</p>
                </div>
            </div>

            <div class="grid grid-cols-4 gap-4">
                <UFormGroup name="title" label="Title" class="col-span-1">
                    <UInput :model-value="state.title" @update:model-value="update('title', $event)" placeholder="Dr." size="lg" />
                </UFormGroup>
                <UFormGroup name="full_name" label="Full name" class="col-span-3" required>
                    <UInput :model-value="state.full_name" @update:model-value="update('full_name', $event)" placeholder="Rania Haddad" size="lg" />
                </UFormGroup>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
                <UFormGroup v-if="mode === 'admin'" name="specialty_id" label="Specialty" :help="specialtyHelp">
                    <USelectMenu
                        :model-value="state.specialty_id"
                        @update:model-value="update('specialty_id', $event)"
                        :options="specialtyOptions"
                        value-attribute="id"
                        option-attribute="name_en"
                        searchable
                        searchable-placeholder="Search specialties..."
                        placeholder="Select a specialty"
                        size="lg"
                    />
                </UFormGroup>
                <UFormGroup v-else label="Specialty" help="Managed by the telemedicine team">
                    <div :class="lockedClass">
                        <UIcon name="i-ph-lock-simple-duotone" class="size-4 flex-shrink-0 text-gray-400" />
                        <span class="truncate">{{ specialtyName || 'Not set' }}</span>
                    </div>
                </UFormGroup>

                <UFormGroup name="license_number" label="License number">
                    <UInput :model-value="state.license_number ?? ''" @update:model-value="update('license_number', $event)" placeholder="e.g. OMSB-12345" size="lg" />
                </UFormGroup>

                <UFormGroup name="years_experience" label="Years of experience">
                    <UInput
                        :model-value="state.years_experience ?? ''"
                        @update:model-value="update('years_experience', $event)"
                        type="number"
                        min="0"
                        max="70"
                        step="1"
                        placeholder="0"
                        size="lg"
                    />
                </UFormGroup>

                <UFormGroup name="languages" label="Languages">
                    <USelectMenu
                        :model-value="state.languages ?? []"
                        @update:model-value="update('languages', $event)"
                        :options="DOCTOR_LANGUAGES"
                        value-attribute="value"
                        option-attribute="label"
                        multiple
                        placeholder="Select languages"
                        size="lg"
                    >
                        <template #label>
                            <span v-if="languageLabels" class="truncate">{{ languageLabels }}</span>
                            <span v-else class="text-gray-400">Select languages</span>
                        </template>
                    </USelectMenu>
                </UFormGroup>
            </div>
        </section>

        <section class="space-y-4">
            <div>
                <h4 class="text-sm font-semibold text-gray-700">About</h4>
                <p class="text-xs text-gray-500">Patients read this before booking. Keep it short and specific.</p>
            </div>
            <UFormGroup name="bio_en" label="Bio (English)">
                <UTextarea
                    :model-value="state.bio_en ?? ''"
                    @update:model-value="update('bio_en', $event)"
                    :rows="4"
                    autoresize
                    placeholder="Training, areas of focus, what patients can expect..."
                    size="lg"
                />
            </UFormGroup>
            <UFormGroup name="bio_ar" label="Bio (Arabic)">
                <UTextarea
                    :model-value="state.bio_ar ?? ''"
                    @update:model-value="update('bio_ar', $event)"
                    :rows="4"
                    autoresize
                    dir="rtl"
                    placeholder="نبذة عن الطبيب..."
                    size="lg"
                />
            </UFormGroup>
        </section>

        <section class="space-y-4">
            <div>
                <h4 class="text-sm font-semibold text-gray-700">Consultation</h4>
                <p v-if="mode === 'admin'" class="text-xs text-gray-500">Fixed price per session, charged to the patient when booking.</p>
                <p v-else class="text-xs text-gray-500">Pricing and session length are managed by the telemedicine team.</p>
            </div>

            <div v-if="mode === 'admin'" class="grid gap-4 sm:grid-cols-3">
                <UFormGroup name="session_price_usd" label="Session price">
                    <UInput
                        :model-value="state.session_price_usd"
                        @update:model-value="update('session_price_usd', $event)"
                        type="number"
                        min="0"
                        step="0.5"
                        size="lg"
                        :ui="{ leading: { padding: { lg: 'ps-8' } }, trailing: { padding: { lg: 'pe-12' } } }"
                    >
                        <template #leading>
                            <span class="text-gray-500">$</span>
                        </template>
                        <template #trailing>
                            <span class="text-xs text-gray-400">USD</span>
                        </template>
                    </UInput>
                </UFormGroup>
                <UFormGroup name="session_duration_min" label="Session duration">
                    <USelectMenu
                        :model-value="state.session_duration_min"
                        @update:model-value="update('session_duration_min', $event)"
                        :options="durationOptions"
                        value-attribute="value"
                        option-attribute="label"
                        size="lg"
                    >
                        <template #label>{{ minutes(state.session_duration_min) }}</template>
                    </USelectMenu>
                </UFormGroup>
                <UFormGroup name="buffer_min" label="Buffer after session">
                    <USelectMenu
                        :model-value="state.buffer_min"
                        @update:model-value="update('buffer_min', $event)"
                        :options="bufferOptions"
                        value-attribute="value"
                        option-attribute="label"
                        size="lg"
                    >
                        <template #label>{{ bufferLabel(state.buffer_min) }}</template>
                    </USelectMenu>
                </UFormGroup>
            </div>

            <div v-else class="grid gap-4 sm:grid-cols-3">
                <UFormGroup v-for="field in lockedPricing" :key="field.label" :label="field.label" help="Managed by the telemedicine team">
                    <div :class="lockedClass">
                        <UIcon name="i-ph-lock-simple-duotone" class="size-4 flex-shrink-0 text-gray-400" />
                        <span class="truncate">{{ field.value }}</span>
                    </div>
                </UFormGroup>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import type { DoctorProfileFields, Specialty } from '~/types/telemedicine'
import { DOCTOR_LANGUAGES } from '~/utils/telemedicineDoctor'

const props = defineProps<{
    modelValue: DoctorProfileFields
    mode: 'admin' | 'doctor'
    specialties: Pick<Specialty, 'id' | 'name_en' | 'active'>[]
    profileId?: string
    specialtiesUnavailable?: boolean
}>()

const emit = defineEmits<{
    'update:modelValue': [DoctorProfileFields]
}>()

const state = computed(() => props.modelValue)

const lockedClass = 'flex items-center gap-2 rounded-md bg-gray-50 px-3.5 py-2.5 text-sm text-gray-700 ring-1 ring-inset ring-gray-200'

const durationOptions = [15, 20, 30, 45, 60].map((value) => ({ value, label: minutes(value) }))
const bufferOptions = [0, 5, 10, 15].map((value) => ({ value, label: bufferLabel(value) }))

const specialtyOptions = computed(() => props.specialties.filter((s) => s.active || s.id === state.value.specialty_id))
const specialtyName = computed(() => props.specialties.find((s) => s.id === state.value.specialty_id)?.name_en)
const specialtyHelp = computed(() => {
    if (props.specialtiesUnavailable) return 'Specialties could not be loaded. Reload the page to try again.'
    return props.specialties.some((s) => s.active) ? undefined : 'No active specialties yet. Add them under Specialties.'
})

const languageLabels = computed(() => DOCTOR_LANGUAGES
    .filter((l) => state.value.languages?.includes(l.value))
    .map((l) => l.label)
    .join(', '))

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

const lockedPricing = computed(() => [
    { label: 'Session price', value: Number(state.value.session_price_usd) > 0 ? usd.format(Number(state.value.session_price_usd)) : 'Not set' },
    { label: 'Session duration', value: minutes(state.value.session_duration_min) },
    { label: 'Buffer after session', value: bufferLabel(state.value.buffer_min) },
])

function minutes(value: number) {
    return `${value} min`
}

function bufferLabel(value: number) {
    return value ? `${value} min` : 'No buffer'
}

function update<K extends keyof DoctorProfileFields>(key: K, value: DoctorProfileFields[K]) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>
